<script lang="ts">
    import { v4 as uuidv4 } from "uuid";
    import { User } from "../Model/User";
    import { usersService } from "../services";
    import { Button, Modal, ModalHeader, ModalBody, TabContent, TabPane, Icon, InputGroup, Badge } from "sveltestrap";
    import AppWrapper from "../components/AppWrapper.svelte";
    import { onMount } from "svelte";
    import UserForm from "../components/User/UserForm.svelte";

    let users: User[] = [];
    let offset = 0;
    let limit = 25;
    let hasMoreUsers = false;
    let isDownloading = false;
    let openUserModal = false;
    let currentUser: User = undefined;
    let isDeleting = false;
    let isNew = false;
    let searchString = '';
    let isSearching = false;

    onMount(getUsers);

    function getMoreUsers() {
        offset += limit;
        getUsers();
    }

    function editUser(user: User): void {
        isNew = false;
        currentUser = user;
        openUserModal = true;
    }

    function newUser() {
        isNew = true;
        currentUser = new User({
            id: uuidv4(),
            access_token: uuidv4(),
            status: true,
            enabled: true,
            removed: false,
            rol_id: 3,
            rol_name: "",
            name: "",
            phone: "",
            dni: "",
            email: "",
            password: "",
            image: "",
            foreman: false,
            date_created: "",
            date_updated: "",
            date_last_login: "",
            date_removed: "",
        });
        openUserModal = true;
    }

    function onDeleted({ detail: user }: CustomEvent<User>): void {
        users = users.filter(({ id }) => id !== user.id);
        currentUser = undefined;
    }

    function toggleUserModal(): void {
        openUserModal = !openUserModal;
    }

    async function onDelete(): Promise<void> {
        if (
            isDeleting ||
            !currentUser ||
            !confirm("¿Seguro que desea eliminar este usuario?")
        ) {
            return;
        }
        isDeleting = true;
        try {
            await usersService.deleteUser(currentUser.id);
            users = users.filter(({ id }) => id !== currentUser.id);
            openUserModal = false;
        } catch (error) {
            console.error(error);
        } finally {
            isDeleting = false;
        }
    }

    function onUserSaved({ detail: user }: CustomEvent<User>): void {
        if (isNew) {
            users = [user, ...users];
        } else {
            const index = users.findIndex(({ id }) => id === user.id);
            if (index !== -1) {
                users.splice(index, 1, user);
                users = users;
            }
        }
        openUserModal = false;
    }

    function onSearch(): void {
        if (isSearching) {
            return;
        }
        offset = 0;
        users = [];
        getUsers();
    }

    async function getUsers(): Promise<void> {
        if (isSearching) {
            return;
        }
        try {
            const chunk = searchString
                ? await usersService.queryUsers(searchString, offset, limit)
                : await usersService.findUsers(offset, limit);
            users = [...users, ...chunk];
            hasMoreUsers = chunk.length === limit;
        } catch (error) {
            console.error(error);
        } finally {
            isSearching = false;
        }
    }

</script>

<AppWrapper>
    <div class="container-fluid">
        <div class="row">
            <div class="col-12 col-sm-4 mb-3">
                <div class="text-start">
                    <h3>Listado de Usuarios</h3>
                </div>
            </div>
            <div class="col-12 col-sm-8 mb-3 d-flex justify-content-end gap-3">
                <form on:submit|preventDefault={onSearch}>
                    <InputGroup>
                        <input type="text" class="form-control" bind:value={searchString} />
                        <Button type="submit" color="primary">
                            <Icon name="search" />
                        </Button>
                    </InputGroup>
                </form>
                <Button color="primary" on:click={newUser}>
                    Nuevo usuario
                </Button>
            </div>
        </div>
        <div class="row">
            <div class="col">
                {#if users?.length}
                    <table class="table">
                        <thead>
                            <tr>
                                <th scope="col">Nombre</th>
                                <th scope="col">Email</th>
                                <th scope="col">DNI</th>
                                <th scope="col">Tipo</th>
                                <th scope="col">Capataz</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each users as user}
                                <tr>
                                    <td>
                                        <span
                                            class="btn btn-link"
                                            on:click={() => editUser(user)}>{user.name}</span>
                                    </td>
                                    <td>{user.email}</td>
                                    <td>{user.dni}</td>
                                    <td>
                                        {user.rol_name}
                                    </td>
                                    <td>
                                        {#if user.foreman}
                                            <Badge color="success">Capataz</Badge>
                                        {/if}
                                    </td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                {:else}
                    <div class="text-center pt-5">
                        <hr />
                        <h3>No hay usuarios para mostrar</h3>
                    </div>
                {/if}
            </div>
        </div>
        {#if hasMoreUsers}
            <div class="row">
                <div class="col text-center">
                    <Button
                        type="button"
                        color="primary"
                        on:click={getMoreUsers}
                        disabled={isDownloading}>
                        {isDownloading ? "Descargando..." : "Ver más"}
                    </Button>
                </div>
            </div>
        {/if}
    </div>
</AppWrapper>

<Modal isOpen={openUserModal} size="lg">
    <ModalHeader toggle={toggleUserModal}><Icon name="person-fill" /> Editar usuario</ModalHeader>
    <ModalBody>
        <TabContent>
            <TabPane tabId="form" tab="Usuario" active class="pt-3">
                <UserForm user={currentUser} on:saved={onUserSaved} />
            </TabPane>
            {#if !isNew}
                <TabPane tabId="remove" tab="Eliminar" class="pt-3">
                    <Button
                        color="danger"
                        size="lg"
                        on:click={onDelete}
                        disabled={isDeleting}>
                        {isDeleting ? "Eliminando..." : "Eliminar"}
                    </Button>
                </TabPane>
            {/if}
        </TabContent>
    </ModalBody>
</Modal>